import type { Explainer } from './explainerRegistry';
import { renderExplainerVisual } from './explainerVisualRegistry';
import type { ScopeSnapshot } from './scopeModel';
import type { SystemOptionCard, HeatDemandData } from './types';
import { QRCode } from '../print/QRCode';
import { buildPortalShareUrl } from '../../utils/deepLink';

interface CurrentSystemInfo {
  label: string;
  strengths: string[];
  weaknesses: string[];
  threats: string[];
}

export interface PrintableReportProps {
  currentSystem: CurrentSystemInfo;
  optionA: SystemOptionCard | null;
  optionB: SystemOptionCard | null;
  optionC?: SystemOptionCard | null;
  scope: ScopeSnapshot | null;
  explainers: Explainer[];
  docId?: string;
  portalBaseUrl?: string;
  generatedAt?: string;
  selectedOptionId?: string | null;
  reportLayout?: 'A4_LANDSCAPE_QUADRANT' | 'V2_DECISION';
  /** Optional heat demand data for the V2 heat-loss summary section */
  heatDemand?: HeatDemandData;
}

const page: React.CSSProperties = { pageBreakAfter: 'always', padding: '12mm', fontFamily: 'system-ui, sans-serif', color: '#0f172a' };
const card = (tone: string): React.CSSProperties => ({ border: `1px solid ${tone}`, borderLeft: `4px solid ${tone}`, borderRadius: 8, padding: 12, minHeight: 0, background: '#fff' });

function score(option: SystemOptionCard | null | undefined): string {
  const v = option?.scoreBreakdown?.overallScore ?? option?.suitabilityScore;
  return typeof v === 'number' && Number.isFinite(v) ? `${Math.round(v)}` : '—';
}

function tradeoffLine(option: SystemOptionCard | null | undefined): string {
  const tradeoff = (option?.wontSolve ?? [])[0];
  return tradeoff && tradeoff.trim().length > 0 ? tradeoff : 'No significant trade-offs identified.';
}

function architectureLabel(level?: SystemOptionCard['architectureLevel']): string {
  if (level === 'compatible') return 'Compatible with current infrastructure';
  if (level === 'moderate_upgrade') return 'Requires moderate emitter upgrades';
  if (level === 'full_redesign') return 'Requires full low-temperature redesign';
  return 'Not assessed';
}

function QuadrantPage({ currentSystem, optionA, optionB, optionC }: { currentSystem: CurrentSystemInfo; optionA: SystemOptionCard | null; optionB: SystemOptionCard | null; optionC: SystemOptionCard | null; }) {
  return <div style={page}>
    <h2 style={{ margin: '0 0 8px' }}>Heating system assessment</h2>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 10, minHeight: '170mm' }}>
      {/* Top-left: Current system */}
      <section style={card('#64748b')}>
        <h3 style={{ margin: '0 0 6px', fontSize: '0.95rem', color: '#475569' }}>Current system</h3>
        <p style={{ margin: '0 0 6px' }}><strong>{currentSystem.label}</strong></p>
        {currentSystem.strengths.length > 0 && (
          <>
            <p style={{ margin: '4px 0 2px', fontSize: '0.8rem', fontWeight: 600, color: '#15803d' }}>Strengths</p>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.8rem' }}>{currentSystem.strengths.slice(0, 3).map((s, i) => <li key={i}>{s}</li>)}</ul>
          </>
        )}
        {currentSystem.weaknesses.length > 0 && (
          <>
            <p style={{ margin: '6px 0 2px', fontSize: '0.8rem', fontWeight: 600, color: '#b91c1c' }}>Weaknesses</p>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.8rem' }}>{currentSystem.weaknesses.slice(0, 3).map((s, i) => <li key={i}>{s}</li>)}</ul>
          </>
        )}
        {currentSystem.threats.length > 0 && (
          <>
            <p style={{ margin: '6px 0 2px', fontSize: '0.8rem', fontWeight: 600, color: '#d97706' }}>Watch out for</p>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.8rem' }}>{currentSystem.threats.slice(0, 2).map((s, i) => <li key={i}>{s}</li>)}</ul>
          </>
        )}
      </section>
      {/* Top-right: Option A */}
      <section style={card('#2563eb')}>
        <h3 style={{ margin: '0 0 6px', fontSize: '0.95rem' }}>Option A — Best fit <span style={{ float: 'right', background: '#2563eb', color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: '0.85rem' }}>{score(optionA)}</span></h3>
        <p style={{ margin: '0 0 6px' }}><strong>{optionA?.label ?? 'Not assessed'}</strong></p>
        <p style={{ margin: '4px 0 2px', fontSize: '0.8rem', fontWeight: 600, color: '#1d4ed8' }}>Gives you</p>
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.8rem' }}>{(optionA?.becauseBullets ?? []).slice(0, 3).map((b, i) => <li key={i}>{b}</li>)}</ul>
        <p style={{ margin: '6px 0 2px', fontSize: '0.8rem', fontWeight: 600, color: '#6b7280' }}>Limits</p>
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.8rem' }}>{(optionA?.wontSolve ?? []).slice(0, 2).map((b, i) => <li key={i}>{b}</li>)}</ul>
      </section>
      {/* Bottom-left: Option C */}
      <section style={card('#0d9488')}>
        <h3 style={{ margin: '0 0 6px', fontSize: '0.95rem' }}>Option C — Cost optimised <span style={{ float: 'right', background: '#0d9488', color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: '0.85rem' }}>{score(optionC)}</span></h3>
        <p style={{ margin: '0 0 6px' }}><strong>{optionC?.label ?? optionA?.label ?? 'Not assessed'}</strong></p>
        <p style={{ margin: '4px 0 2px', fontSize: '0.8rem', fontWeight: 600, color: '#0f766e' }}>Benefits</p>
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.8rem' }}>{(optionC?.becauseBullets ?? ['Same architecture as A with trimmed non-essential upgrades.']).slice(0, 4).map((b, i) => <li key={i}>{b}</li>)}</ul>
        {(optionC?.wontSolve ?? []).length > 0 && (
          <>
            <p style={{ margin: '6px 0 2px', fontSize: '0.8rem', fontWeight: 600, color: '#6b7280' }}>Trade-offs</p>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.8rem' }}>{(optionC?.wontSolve ?? []).slice(0, 2).map((b, i) => <li key={i}>{b}</li>)}</ul>
          </>
        )}
      </section>
      {/* Bottom-right: Option B */}
      <section style={card('#7c3aed')}>
        <h3 style={{ margin: '0 0 6px', fontSize: '0.95rem' }}>Option B — Next best <span style={{ float: 'right', background: '#7c3aed', color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: '0.85rem' }}>{score(optionB)}</span></h3>
        <p style={{ margin: '0 0 6px' }}><strong>{optionB?.label ?? 'Not assessed'}</strong></p>
        <p style={{ margin: '4px 0 2px', fontSize: '0.8rem', fontWeight: 600, color: '#5b21b6' }}>Still gives you</p>
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.8rem' }}>{(optionB?.becauseBullets ?? []).slice(0, 2).map((b, i) => <li key={i}>{b}</li>)}</ul>
        <p style={{ margin: '6px 0 2px', fontSize: '0.8rem', fontWeight: 600, color: '#6b7280' }}>Why it scores lower</p>
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.8rem' }}>{(optionB?.wontSolve ?? []).slice(0, 3).map((b, i) => <li key={i}>{b}</li>)}</ul>
      </section>
    </div>
  </div>;
}

function scoreDetail(option: SystemOptionCard | null | undefined): { tech: string; priority: string; overall: string } {
  const breakdown = option?.scoreBreakdown;
  const overall = typeof breakdown?.overallScore === 'number' ? `${Math.round(breakdown.overallScore)}` : score(option);
  const tech = typeof breakdown?.technicalFit === 'number' ? `${Math.round(breakdown.technicalFit)}` : '—';
  const priority = typeof breakdown?.priorityFit === 'number' ? `${Math.round(breakdown.priorityFit)}` : '—';
  return { overall, tech, priority };
}

// Shared style for the "Open interactive report" link in printed pages.
// Uses nowrap + ellipsis so PDF text selection never captures a truncated URL or trailing whitespace.
const portalLinkStyle: React.CSSProperties = {
  fontSize: '0.82rem',
  color: '#2563eb',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'inline-block',
  maxWidth: '100%',
};

function MathsPage({ docId, portalBaseUrl, optionA, optionB, optionC }: { docId?: string; portalBaseUrl?: string; optionA: SystemOptionCard | null; optionB: SystemOptionCard | null; optionC: SystemOptionCard | null; }) {
  const fullUrl = docId ? buildPortalShareUrl(docId, portalBaseUrl ?? (typeof window !== 'undefined' ? window.location.origin : '')) : '';

  const cellStyle: React.CSSProperties = { padding: '6px 10px', border: '1px solid #e2e8f0', fontSize: '0.82rem' };
  const headStyle: React.CSSProperties = { ...cellStyle, background: '#f1f5f9', fontWeight: 700, textAlign: 'left' };

  const sdA = scoreDetail(optionA);
  const sdB = scoreDetail(optionB);
  const sdC = scoreDetail(optionC);

  return <div style={page}>
    <h2 style={{ margin: '0 0 12px' }}>Summary &amp; Technical Detail</h2>

    {/* ── Summary comparison ── */}
    <section style={{ marginBottom: 18 }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '0.9rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Option comparison summary</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={headStyle}>Option</th>
            <th style={headStyle}>System</th>
            <th style={headStyle}>Key benefit</th>
            <th style={headStyle}>Key trade-off</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ ...cellStyle, color: '#2563eb', fontWeight: 700 }}>A</td>
            <td style={cellStyle}>{optionA?.label ?? '—'}</td>
            <td style={cellStyle}>{(optionA?.becauseBullets ?? [])[0] ?? '—'}</td>
            <td style={cellStyle}>{(optionA?.wontSolve ?? [])[0] ?? '—'}</td>
          </tr>
          <tr style={{ background: '#fafafa' }}>
            <td style={{ ...cellStyle, color: '#0d9488', fontWeight: 700 }}>C</td>
            <td style={cellStyle}>{optionC?.label ?? optionA?.label ?? '—'}</td>
            <td style={cellStyle}>{(optionC?.becauseBullets ?? [])[0] ?? 'Cost-optimised variant of A'}</td>
            <td style={cellStyle}>{tradeoffLine(optionC)}</td>
          </tr>
          <tr>
            <td style={{ ...cellStyle, color: '#7c3aed', fontWeight: 700 }}>B</td>
            <td style={cellStyle}>{optionB?.label ?? '—'}</td>
            <td style={cellStyle}>{(optionB?.becauseBullets ?? [])[0] ?? '—'}</td>
            <td style={cellStyle}>{tradeoffLine(optionB)}</td>
          </tr>
        </tbody>
      </table>
    </section>

    {/* ── Technical scoring ── */}
    <section style={{ marginBottom: 18 }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '0.9rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Technical scoring</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={headStyle}>Option</th>
            <th style={{ ...headStyle, textAlign: 'center' }}>Overall score</th>
            <th style={{ ...headStyle, textAlign: 'center' }}>Technical fit</th>
            <th style={{ ...headStyle, textAlign: 'center' }}>Priority fit</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ ...cellStyle, color: '#2563eb', fontWeight: 700 }}>Option A — {optionA?.label ?? 'Not assessed'}</td>
            <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 700 }}>{sdA.overall}</td>
            <td style={{ ...cellStyle, textAlign: 'center' }}>{sdA.tech}</td>
            <td style={{ ...cellStyle, textAlign: 'center' }}>{sdA.priority}</td>
          </tr>
          <tr style={{ background: '#fafafa' }}>
            <td style={{ ...cellStyle, color: '#0d9488', fontWeight: 700 }}>Option C — {optionC?.label ?? optionA?.label ?? 'Not assessed'}</td>
            <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 700 }}>{sdC.overall}</td>
            <td style={{ ...cellStyle, textAlign: 'center' }}>{sdC.tech}</td>
            <td style={{ ...cellStyle, textAlign: 'center' }}>{sdC.priority}</td>
          </tr>
          <tr>
            <td style={{ ...cellStyle, color: '#7c3aed', fontWeight: 700 }}>Option B — {optionB?.label ?? 'Not assessed'}</td>
            <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 700 }}>{sdB.overall}</td>
            <td style={{ ...cellStyle, textAlign: 'center' }}>{sdB.tech}</td>
            <td style={{ ...cellStyle, textAlign: 'center' }}>{sdB.priority}</td>
          </tr>
        </tbody>
      </table>
      <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
        Scores are out of 100. Technical fit = system compatibility with property. Priority fit = alignment with stated customer priorities.
      </p>
    </section>

    {/* ── Score sub-breakdown (Option A detail) ── */}
    {optionA?.scoreBreakdown?.details?.technicalSubScores && Object.keys(optionA.scoreBreakdown.details.technicalSubScores).length > 0 && (
      <section style={{ marginBottom: 18 }}>
        <h3 style={{ margin: '0 0 6px', fontSize: '0.9rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Option A technical sub-scores</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {Object.entries(optionA.scoreBreakdown.details.technicalSubScores).map(([key, val]) => (
              <tr key={key}>
                <td style={{ ...cellStyle, color: '#374151' }}>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 600 }}>{typeof val === 'number' ? Math.round(val as number) : String(val)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    )}

    {/* ── QR code + link ── */}
    {docId && (
      <section style={{ display: 'flex', gap: 20, alignItems: 'flex-start', paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
        <QRCode url={fullUrl} size={120} />
        <div>
          <h3 style={{ margin: '0 0 6px', fontSize: '0.9rem' }}>Full interactive report</h3>
          <p style={{ margin: '0 0 4px', fontSize: '0.82rem', color: '#475569' }}>Scan the QR code or visit the link below to access the full report, animations and detailed explanations.</p>
          <a href={fullUrl} style={portalLinkStyle}>Open interactive report</a>
        </div>
      </section>
    )}
  </div>;
}

function getRelevantExplainers(explainers: Explainer[], chosenOption: SystemOptionCard | null): Explainer[] {
  const id = (chosenOption?.id ?? '').toLowerCase();
  return explainers.filter((exp) => {
    const t = `${exp.title} ${exp.description}`.toLowerCase();
    if (id.includes('combi') && t.includes('combi')) return true;
    if ((id.includes('unvented') || id.includes('stored')) && t.includes('stored')) return true;
    if (t.includes('sealed') || t.includes('open vented') || t.includes('load compensation')) return true;
    return false;
  });
}

function ExplainerPage({ explainer }: { explainer: Explainer }) {
  return <div style={page}><h2>{explainer.title}</h2>{explainer.content.map((block, i) => {
    if (block.type === 'text') return <p key={i}>{block.body}</p>;
    if (block.type === 'animation' || block.type === 'diagram' || block.type === 'chart') return <div key={i}>{renderExplainerVisual(block.componentKey)}</div>;
    return null;
  })}</div>;
}

// ── V2 Report: Heat loss sizing constants ──────────────────────────

/** Low-demand threshold (kW): below this is "Low demand" */
const LOW_DEMAND_KW = 8;
/** Medium-demand upper bound (kW): above this is "Higher demand" */
const MEDIUM_DEMAND_KW = 14;
/** Sizing headroom applied on top of declared heat loss (20%) */
const HEAT_PUMP_SIZING_HEADROOM = 0.20;

// ── V2 Report: Decision Page (Page 1, customer-facing) ─────────────

function heatLossBandLabel(band: string): string {
  switch (band) {
    case 'very-low': case 'low': return `Low demand (<${LOW_DEMAND_KW} kW typical)`;
    case 'medium': return `Typical demand (${LOW_DEMAND_KW}–${MEDIUM_DEMAND_KW} kW)`;
    case 'high': case 'very-high': return `Higher demand (>${MEDIUM_DEMAND_KW} kW) — verify emitters before installation`;
    default: return 'Demand band not assessed';
  }
}

function V2DecisionPage({ currentSystem, optionA, optionB, optionC, heatDemand }: {
  currentSystem: CurrentSystemInfo;
  optionA: SystemOptionCard | null;
  optionB: SystemOptionCard | null;
  optionC: SystemOptionCard | null;
  heatDemand?: HeatDemandData;
}) {
  const pageStyle: React.CSSProperties = { ...page, pageBreakAfter: 'always' };
  const headingStyle: React.CSSProperties = { margin: '0 0 16px', fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', borderBottom: '2px solid #2563eb', paddingBottom: 8 };
  const sectionTitle: React.CSSProperties = { margin: '0 0 8px', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' };

  // Heat loss summary
  const manualKw = heatDemand?.knownHeatLossKw;
  const sizingTarget = manualKw != null ? manualKw * (1 + HEAT_PUMP_SIZING_HEADROOM) : null;

  return (
    <div style={pageStyle}>
      <h1 style={headingStyle}>Heating System Recommendation</h1>

      {/* Section 1: Current system snapshot */}
      <section style={{ marginBottom: 18 }}>
        <h2 style={sectionTitle}>Current System</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ background: '#f8fafc', borderRadius: 6, padding: '8px 12px' }}>
            <p style={{ margin: '0 0 4px', fontSize: '0.8rem', fontWeight: 700, color: '#15803d' }}>Strengths</p>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.8rem', color: '#374151' }}>
              {currentSystem.strengths.slice(0, 3).map((s, i) => <li key={i}>{s}</li>)}
              {currentSystem.strengths.length === 0 && <li style={{ color: '#9ca3af' }}>None noted</li>}
            </ul>
          </div>
          <div style={{ background: '#f8fafc', borderRadius: 6, padding: '8px 12px' }}>
            <p style={{ margin: '0 0 4px', fontSize: '0.8rem', fontWeight: 700, color: '#b45309' }}>Watch-outs</p>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.8rem', color: '#374151' }}>
              {[...currentSystem.weaknesses, ...currentSystem.threats].slice(0, 3).map((s, i) => <li key={i}>{s}</li>)}
              {currentSystem.weaknesses.length === 0 && currentSystem.threats.length === 0 && <li style={{ color: '#9ca3af' }}>None noted</li>}
            </ul>
          </div>
        </div>
      </section>

      {/* Section 2: Heat Loss Summary */}
      {heatDemand && (
        <section style={{ marginBottom: 18, background: '#f0f9ff', borderRadius: 8, padding: '10px 14px', border: '1px solid #bae6fd' }}>
          <h2 style={{ ...sectionTitle, color: '#0369a1' }}>Heat Loss Summary</h2>
          {manualKw != null && manualKw > 0 ? (
            <>
              <p style={{ margin: '0 0 4px', fontSize: '0.85rem' }}>
                <strong>Design heat loss: {manualKw.toFixed(1)} kW</strong>
                <span style={{ marginLeft: 10, fontSize: '0.75rem', color: '#0369a1', background: '#e0f2fe', borderRadius: 4, padding: '1px 6px' }}>High confidence (manual entry)</span>
              </p>
              {sizingTarget != null && (
                <p style={{ margin: '0 0 4px', fontSize: '0.85rem' }}>
                  Recommended sizing target (with {Math.round(HEAT_PUMP_SIZING_HEADROOM * 100)}% headroom): <strong>{sizingTarget.toFixed(1)} kW</strong>
                </p>
              )}
            </>
          ) : (
            <>
              <p style={{ margin: '0 0 4px', fontSize: '0.85rem' }}>
                <strong>Estimated heat loss band: {heatLossBandLabel(heatDemand.heatLossBand)}</strong>
                <span style={{ marginLeft: 10, fontSize: '0.75rem', color: '#92400e', background: '#fef3c7', borderRadius: 4, padding: '1px 6px' }}>Medium/Low confidence — based on inferred property details</span>
              </p>
            </>
          )}
          <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#475569' }}>
            {manualKw != null && manualKw > 0
              ? 'Declared heat loss from survey/design. Final sizing confirmed before installation.'
              : 'Some inputs were inferred. Final sizing will be verified before installation.'}
          </p>
        </section>
      )}

      {/* Section 3: Recommended option (Option A — dominant) */}
      <section style={{ marginBottom: 18, background: '#eff6ff', border: '2px solid #2563eb', borderRadius: 8, padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <h2 style={{ ...sectionTitle, color: '#1d4ed8', margin: 0 }}>Recommended Option</h2>
          {optionA && (
            <span style={{ background: '#2563eb', color: '#fff', borderRadius: 20, padding: '2px 12px', fontSize: '1rem', fontWeight: 800 }}>
              {score(optionA)}/100
            </span>
          )}
        </div>
        <p style={{ margin: '0 0 8px', fontSize: '1rem', fontWeight: 700 }}>
          Option A — {optionA?.label ?? 'Not assessed'}
        </p>
        <ul style={{ margin: '0 0 8px', paddingLeft: 18, fontSize: '0.85rem' }}>
          {(optionA?.becauseBullets ?? []).slice(0, 3).map((b, i) => <li key={i}>{b}</li>)}
        </ul>
        {(optionA?.wontSolve ?? []).length > 0 && (
          <>
            <p style={{ margin: '0 0 2px', fontSize: '0.78rem', fontWeight: 600, color: '#6b7280' }}>Limits / trade-offs</p>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.78rem', color: '#6b7280' }}>
              {(optionA?.wontSolve ?? []).slice(0, 3).map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </>
        )}
      </section>

      {/* Section 4: Alternatives */}
      <section>
        <h2 style={sectionTitle}>Alternatives</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {/* Option B */}
          <div style={{ border: '1px solid #7c3aed', borderLeft: '4px solid #7c3aed', borderRadius: 8, padding: '10px 12px', background: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 700 }}>Option B — {optionB?.label ?? 'Not assessed'}</span>
              <span style={{ background: '#7c3aed', color: '#fff', borderRadius: 12, padding: '1px 8px', fontSize: '0.8rem' }}>{score(optionB)}</span>
            </div>
            <p style={{ margin: '0 0 4px', fontSize: '0.78rem', color: '#5b21b6' }}>{(optionB?.becauseBullets ?? [])[0] ?? '—'}</p>
            <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>Trade-off: {tradeoffLine(optionB)}</p>
          </div>
          {/* Option C */}
          <div style={{ border: '1px solid #0d9488', borderLeft: '4px solid #0d9488', borderRadius: 8, padding: '10px 12px', background: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 700 }}>Option C — {optionC?.label ?? optionA?.label ?? 'Not assessed'}</span>
              <span style={{ background: '#0d9488', color: '#fff', borderRadius: 12, padding: '1px 8px', fontSize: '0.8rem' }}>{score(optionC)}</span>
            </div>
            <p style={{ margin: '0 0 4px', fontSize: '0.78rem', color: '#0f766e' }}>{(optionC?.becauseBullets ?? [])[0] ?? 'Cost-optimised variant of A'}</p>
            <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>Trade-off: {tradeoffLine(optionC)}</p>
            {optionC?.id?.startsWith('ashp') && (
              <div style={{ marginTop: 6, fontSize: '0.74rem', color: '#334155' }}>
                <p style={{ margin: '0 0 2px' }}><strong>Retrofit Score:</strong> {optionC.retrofitScore ?? score(optionC)} — {optionC.scoreLabel ?? 'Significant Compromises'}</p>
                <p style={{ margin: '0 0 2px' }}><strong>Redesigned System Score:</strong> {optionC.redesignedScore ?? score(optionC)} — {optionC.redesignedScore ? (optionC.redesignedScore >= 75 ? 'Strong Fit' : optionC.redesignedScore >= 60 ? 'Acceptable with Trade-offs' : 'Significant Compromises') : 'Not assessed'}</p>
                <p style={{ margin: '0 0 2px' }}><em>If redesigned for low-temperature operation (≤45°C flow), this system can deliver stable, predictable comfort and reduced carbon impact.</em></p>
                <p style={{ margin: 0 }}><strong>Architecture Required:</strong> {architectureLabel(optionC.architectureLevel)}</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

// ── V2 Report: Technical Detail Page (Page 2, engineer-readable) ───

function V2TechnicalPage({ docId, portalBaseUrl, optionA, optionB, optionC }: {
  docId?: string;
  portalBaseUrl?: string;
  optionA: SystemOptionCard | null;
  optionB: SystemOptionCard | null;
  optionC: SystemOptionCard | null;
}) {
  const resolvedPortalBaseUrl = portalBaseUrl ?? (typeof window !== 'undefined' ? window.location.origin : '');
  const fullUrl = docId ? buildPortalShareUrl(docId, resolvedPortalBaseUrl) : '';
  const pageStyle: React.CSSProperties = { ...page, pageBreakAfter: 'always' };
  const sectionTitle: React.CSSProperties = { margin: '0 0 8px', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' };
  const cellStyle: React.CSSProperties = { padding: '6px 10px', border: '1px solid #e2e8f0', fontSize: '0.82rem' };
  const headStyle: React.CSSProperties = { ...cellStyle, background: '#f1f5f9', fontWeight: 700, textAlign: 'left' };

  const sdA = scoreDetail(optionA);
  const sdB = scoreDetail(optionB);
  const sdC = scoreDetail(optionC);

  return (
    <div style={pageStyle}>
      <h2 style={{ margin: '0 0 14px', fontSize: '1.1rem', fontWeight: 700, borderBottom: '1px solid #e2e8f0', paddingBottom: 6 }}>Technical Detail</h2>

      {/* Option comparison table */}
      <section style={{ marginBottom: 18 }}>
        <h3 style={sectionTitle}>Option comparison</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={headStyle}>Option</th>
              <th style={headStyle}>System</th>
              <th style={{ ...headStyle, textAlign: 'center' }}>Overall</th>
              <th style={{ ...headStyle, textAlign: 'center' }}>Technical fit</th>
              <th style={{ ...headStyle, textAlign: 'center' }}>Priority fit</th>
              <th style={headStyle}>Key benefit</th>
              <th style={headStyle}>Key trade-off</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ ...cellStyle, color: '#2563eb', fontWeight: 700 }}>A</td>
              <td style={cellStyle}>{optionA?.label ?? '—'}</td>
              <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 700 }}>{sdA.overall}</td>
              <td style={{ ...cellStyle, textAlign: 'center' }}>{sdA.tech}</td>
              <td style={{ ...cellStyle, textAlign: 'center' }}>{sdA.priority}</td>
              <td style={cellStyle}>{(optionA?.becauseBullets ?? [])[0] ?? '—'}</td>
              <td style={cellStyle}>{(optionA?.wontSolve ?? [])[0] ?? '—'}</td>
            </tr>
            <tr style={{ background: '#fafafa' }}>
              <td style={{ ...cellStyle, color: '#0d9488', fontWeight: 700 }}>C</td>
              <td style={cellStyle}>{optionC?.label ?? optionA?.label ?? '—'}</td>
              <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 700 }}>{sdC.overall}</td>
              <td style={{ ...cellStyle, textAlign: 'center' }}>{sdC.tech}</td>
              <td style={{ ...cellStyle, textAlign: 'center' }}>{sdC.priority}</td>
              <td style={cellStyle}>{(optionC?.becauseBullets ?? [])[0] ?? 'Cost-optimised variant of A'}</td>
              <td style={cellStyle}>{tradeoffLine(optionC)}</td>
            </tr>
            <tr>
              <td style={{ ...cellStyle, color: '#7c3aed', fontWeight: 700 }}>B</td>
              <td style={cellStyle}>{optionB?.label ?? '—'}</td>
              <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 700 }}>{sdB.overall}</td>
              <td style={{ ...cellStyle, textAlign: 'center' }}>{sdB.tech}</td>
              <td style={{ ...cellStyle, textAlign: 'center' }}>{sdB.priority}</td>
              <td style={cellStyle}>{(optionB?.becauseBullets ?? [])[0] ?? '—'}</td>
              <td style={cellStyle}>{tradeoffLine(optionB)}</td>
            </tr>
          </tbody>
        </table>
        <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: '#94a3b8' }}>
          Scores are out of 100. Technical fit = system compatibility with property. Priority fit = alignment with stated customer priorities.
        </p>
      </section>

      {/* Option A sub-score breakdown */}
      {optionA?.scoreBreakdown?.details?.technicalSubScores && Object.keys(optionA.scoreBreakdown.details.technicalSubScores).length > 0 && (
        <section style={{ marginBottom: 18 }}>
          <h3 style={sectionTitle}>Option A — technical sub-scores</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {Object.entries(optionA.scoreBreakdown.details.technicalSubScores).slice(0, 6).map(([key, val]) => (
                <tr key={key}>
                  <td style={{ ...cellStyle, color: '#374151' }}>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                  <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 600 }}>{typeof val === 'number' ? Math.round(val as number) : String(val)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Assumptions / confidence note */}
      {(optionA?.assumptions ?? []).length > 0 && (
        <section style={{ marginBottom: 18 }}>
          <h3 style={sectionTitle}>Assumptions &amp; confidence notes</h3>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.8rem', color: '#475569' }}>
            {(optionA?.assumptions ?? []).slice(0, 6).map((a, i) => <li key={i}>{a}</li>)}
          </ul>
          <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
            Some inputs may have been inferred from typical property profiles. Final sizing will be confirmed before installation.
          </p>
        </section>
      )}

      {/* QR + portal URL */}
      {docId && fullUrl && (
        <section style={{ display: 'flex', gap: 20, alignItems: 'flex-start', paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
          <QRCode url={fullUrl} size={120} />
          <div>
            <h3 style={{ margin: '0 0 6px', fontSize: '0.9rem' }}>Full interactive report</h3>
            <p style={{ margin: '0 0 4px', fontSize: '0.82rem', color: '#475569' }}>
              Scan the QR code or visit the link below to access the full report with animations and detailed explanations.
            </p>
            <a href={fullUrl} style={portalLinkStyle}>Open interactive report</a>
          </div>
        </section>
      )}
    </div>
  );
}

export function PrintableReport({ currentSystem, optionA, optionB, optionC = null, explainers, docId, portalBaseUrl = '', selectedOptionId, reportLayout = 'V2_DECISION', heatDemand }: PrintableReportProps) {
  const chosenOption = selectedOptionId ? [optionA, optionB, optionC].find((o) => o?.id === selectedOptionId) ?? optionA : optionA;
  const selectedExplainers = getRelevantExplainers(explainers, chosenOption ?? null);

  return <div className="printable-report" style={{ background: '#fff' }}>
    <style>{`@media print { @page { size: A4 landscape; margin: 8mm; } }`}</style>
    {reportLayout === 'V2_DECISION' && (
      <>
        <V2DecisionPage currentSystem={currentSystem} optionA={optionA} optionB={optionB} optionC={optionC} heatDemand={heatDemand} />
        <V2TechnicalPage docId={docId} portalBaseUrl={portalBaseUrl} optionA={optionA} optionB={optionB} optionC={optionC} />
        <div style={{ marginTop: 8 /* small separator below last page */, textAlign: 'right', fontSize: '0.65rem', color: '#94a3b8' }}>Report layout: V2</div>
      </>
    )}
    {reportLayout === 'A4_LANDSCAPE_QUADRANT' && <QuadrantPage currentSystem={currentSystem} optionA={optionA} optionB={optionB} optionC={optionC} />}
    {reportLayout !== 'V2_DECISION' && (
      <MathsPage docId={docId} portalBaseUrl={portalBaseUrl} optionA={optionA} optionB={optionB} optionC={optionC} />
    )}
    {reportLayout !== 'V2_DECISION' && selectedExplainers.map((exp) => <ExplainerPage key={exp.id} explainer={exp} />)}
  </div>;
}
